import { EvidenceBlob, ScanResult } from './martech-scanner-constants';
import {
  assessHeadlessArchitecture,
  buildAccessChecklist,
  generateInsights,
  mergeDetections,
  mergeExtractedIds,
  runDetectionPipeline
} from './martech-scanner-utils';
import { extractTagFires, mergeTagFires } from './tag-fire-utils';
import { buildRenderChecks, summarizeChecks } from './site-health-utils';
import { buildDataLayerInventory, mergeDataLayerInventories } from './datalayer-inventory-utils';
import { FullScanResult } from '../full-scan/full-scan-constants';

/**
 * Merges a completed full-scan crawl's evidence into an existing quick-scan
 * ScanResult. This is a partial merge, not a full replace: the full scan
 * doesn't produce per-page timing metrics or robots/sitemap discoverability
 * facts, so pageMetrics/discoverability are cleared rather than carried over
 * from the quick scan - they were computed against the quick scan's page
 * set, which is a different set of pages than `pagesScanned` below once a
 * full scan has run (e.g. AuditReportView renders pagesScanned.length right
 * next to a pageMetrics-derived median response time; carrying over stale
 * pageMetrics would pair the full scan's page count with the quick scan's
 * timing data).
 *
 * Everything else derivable from real page evidence (detections, extracted
 * IDs, tag fires, the tagging-variable inventory, render checks) is UNIONED
 * with the quick scan's own findings, not replaced by the full scan's alone.
 * The full scan discovers pages from sitemap.xml only (see
 * full-site-scan-design.md), which can surface a smaller or different page
 * set than the quick scan's HTML link-extraction - its evidence isn't
 * guaranteed to be a superset, so a plain replace risks *losing* platforms/
 * tags the quick scan already confirmed (reported live on a real scan of
 * onceuponafarmorganics.com, which lost most of its quick-scan findings once
 * the full scan - crawling a different, narrower page set - merged in).
 * Detections and tag fires additionally carry which scan(s) actually
 * produced them (`seenIn`), so the UI can flag a finding the full scan
 * never re-confirmed, or one it found that the quick scan missed.
 */
export const mergeFullScanIntoScanResult = (
  previous: ScanResult,
  fullScan: FullScanResult
): ScanResult => {
  const evidenceBlobs: EvidenceBlob[] = fullScan.pages.flatMap(page => page.evidenceBlobs);
  const requests = fullScan.pages.flatMap(page => page.requests);
  const postConsentRequests = fullScan.pages.flatMap(page => page.postConsentRequests);
  const dataLayerSnapshot = fullScan.pages.flatMap(page => page.dataLayerSnapshot ?? []);
  const postConsentDataLayerSnapshot = fullScan.pages.flatMap(
    page => page.postConsentDataLayerSnapshot ?? []
  );

  const fullScanPipeline = runDetectionPipeline(
    evidenceBlobs,
    fullScan.scriptSrcs,
    fullScan.domain
  );
  const fullScanTagFires = extractTagFires(evidenceBlobs, requests, postConsentRequests);

  const detections = mergeDetections(previous.detections, fullScanPipeline.detections);
  const extractedIds = mergeExtractedIds(previous.extractedIds, fullScanPipeline.extractedIds);
  const headlessAssessment = assessHeadlessArchitecture(detections);
  const tagFires = mergeTagFires(previous.tagFires ?? [], fullScanTagFires);

  const dataLayerVariables = mergeDataLayerInventories(
    previous.dataLayerVariables ?? { available: [], consumed: [], unused: [], sources: {} },
    buildDataLayerInventory(evidenceBlobs, tagFires, {
      preConsent: dataLayerSnapshot,
      postConsent: postConsentDataLayerSnapshot
    })
  );

  const renderChecksByUrl = new Map(
    (previous.renderSummary?.checks ?? []).map(check => [check.url, check])
  );
  buildRenderChecks(evidenceBlobs).forEach(check => renderChecksByUrl.set(check.url, check));
  const renderSummary = summarizeChecks([...renderChecksByUrl.values()]);

  return {
    ...previous,
    url: fullScan.url,
    domain: fullScan.domain,
    scannedAt: fullScan.scannedAt,
    pagesScanned: Array.from(
      new Set([...previous.pagesScanned, ...fullScan.pages.map(page => page.url)])
    ),
    // js-post-consent: blobs (see runtimeCapture.ts) are still JS evidence,
    // just observed after a consent-accept click - count them too. A plain
    // max (not a true union - the quick scan's own evidenceBlobs aren't
    // retained to dedupe against) at least keeps this from regressing.
    jsFilesScanned: Math.max(
      previous.jsFilesScanned,
      evidenceBlobs.filter(
        b => b.source.startsWith('js:') || b.source.startsWith('js-post-consent:')
      ).length
    ),
    detections,
    extractedIds,
    headlessAssessment,
    accessChecklist: buildAccessChecklist(detections),
    advertiserInsights: generateInsights(detections, extractedIds, headlessAssessment),
    tagFires,
    dataLayerVariables,
    renderSummary,
    runtimeNetwork: {
      thirdPartyHosts: Array.from(
        new Set(fullScan.pages.flatMap(page => page.thirdPartyHosts))
      ).sort(),
      firstPartyHosts: Array.from(
        new Set(fullScan.pages.flatMap(page => page.firstPartyHosts))
      ).sort(),
      requests,
      dataLayerSnapshot,
      postConsentDataLayerSnapshot
    },
    errors: [
      ...previous.errors,
      ...fullScan.errors,
      ...fullScan.pages.flatMap(page => page.errors)
    ],
    pageMetrics: undefined,
    discoverability: undefined
  };
};
